import { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import { FloorplanBusyRooms, FloorplanOccupancyLegend, hasItemOccupancy, hasOccupancyData } from "./FloorplanOccupancyOverlay";
import { FloorplanAlarmLegend, FloorplanAlarmRegions, resolveAlarmRegions } from "./FloorplanAlarmOverlay";
import { FloorplanRoomOverlay } from "./FloorplanRoomOverlay";
import { foldMergedRooms, resolveMergedRoomId, roomZoomPoints } from "./mergedRooms";
import { FurnitureOverlay } from "./furniture/FurnitureOverlay";
import { DeviceOverlay } from "./DeviceOverlay";
import { OutletOverlay } from "./OutletOverlay";
import type { ConnectionStop, ConnectionZone, FloorplanRoom, FloorplanViewerProps } from "./types";
import { FloorplanAreaLabels, FloorplanDoorMarkers } from "./FloorplanStaticMarks";
import { useFloorplanZoom } from "./useFloorplanZoom";
import { viewerLocationData } from "./viewerLocations";
import { ActionIcon, Box, Group, Select, Tooltip } from "@mantine/core";
import { Maximize } from "lucide-react";
import { LocationPicker, darkComboboxProps, darkSchemeScope, type LocationPickerAction, type LocationRef } from "@nodus/ui";

/** The room finder offers the plan's floors (per address) and its rooms. */
const FINDER_KINDS = ["floor", "room"] as const;

function parseViewBox(svgString: string): string | undefined {
  const match = svgString.match(/viewBox=["']([^"']+)["']/);
  return match?.[1];
}

function extractSvgContent(svgString: string): string {
  const openTag = svgString.match(/<svg[^>]*>/);
  if (!openTag) return "";
  const start = openTag.index! + openTag[0].length;
  const end = svgString.lastIndexOf("</svg>");
  if (end === -1) return "";
  return svgString.slice(start, end);
}



interface ViewerPanelProps {
  mgmt?: boolean;
  showRoomOverlays: boolean;
  onToggleRoomOverlays: (v: boolean) => void;
  showAddressLabels: boolean;
  onToggleAddressLabels: (v: boolean) => void;
  showM2: boolean;
  onToggleM2: (v: boolean) => void;
  showFurniture: boolean;
  onToggleFurniture: (v: boolean) => void;
  showEquipment: boolean;
  onToggleEquipment: (v: boolean) => void;
  showDesks: boolean;
  onToggleDesks: (v: boolean) => void;
  showConnections: boolean;
  onToggleConnections: (v: boolean) => void;
  showNavigation: boolean;
  onToggleNavigation: (v: boolean) => void;
  showSvgText: boolean;
  onToggleSvgText: (v: boolean) => void;
  showOutlets: boolean;
  showDevices: boolean;
  /** The Occupancy layer: only offered when a room carries a sensor reading. */
  hasOccupancy?: boolean;
  showOccupancy?: boolean;
  onToggleOccupancy?: (v: boolean) => void;
  onToggleOutlets: (v: boolean) => void;
  onToggleDevices: (v: boolean) => void;
  showTenantNames: boolean;
  onToggleTenantNames: (v: boolean) => void;
  hasNavigation: boolean;
  hasSvgText: boolean;
  roomTypes: string[];
  hiddenRoomTypes: Set<string>;
  onToggleRoomType: (type: string) => void;
  /** Master rooms toggle propagation: clears or fills `hiddenRoomTypes` so
   *  the per-room-type checkboxes visually mirror the master Rooms toggle. */
  onSetAllRoomTypesVisible: (visible: boolean) => void;
  /** Backend-driven layer registry. When present, the layer panel
   *  renders one row per registry entry (filtered to caller's role)
   *  instead of the hardcoded toggles. alwaysShow layers render an
   *  "Always on" indicator with no toggle. */
  availableLayers?: import("./types").FloorplanLayer[];
}

/** Map a registry layer's `type` to the matching legacy show* flag. */
function layerToShowState(
  type: string,
  flags: {
    rooms: boolean;
    addresses: boolean;
    furniture: boolean;
    equipment: boolean;
    desks: boolean;
    connections: boolean;
    navigation: boolean;
    svg_text: boolean;
    outlets: boolean;
    devices: boolean;
    tenant_names: boolean;
  },
): boolean {
  switch (type) {
    case "rooms": return flags.rooms;
    case "addresses": return flags.addresses;
    case "furniture": return flags.furniture;
    case "equipment": return flags.equipment;
    case "desks": return flags.desks;
    case "connections": return flags.connections;
    case "navigation": return flags.navigation;
    case "svg_text": return flags.svg_text;
    case "tenant_names": return flags.tenant_names;
    case "outlets": return flags.outlets;
    case "devices": return flags.devices;
    default: return false;
  }
}

function ViewerPanel({ mgmt, showRoomOverlays, onToggleRoomOverlays, showAddressLabels, onToggleAddressLabels, showM2: _, onToggleM2, showFurniture, onToggleFurniture, showEquipment, onToggleEquipment, showDesks, onToggleDesks, showConnections, onToggleConnections, showNavigation, onToggleNavigation, showSvgText, onToggleSvgText, showOutlets, onToggleOutlets, showDevices, onToggleDevices, hasOccupancy, showOccupancy, onToggleOccupancy, showTenantNames, onToggleTenantNames, hasNavigation, hasSvgText, roomTypes, hiddenRoomTypes, onToggleRoomType, onSetAllRoomTypesVisible, availableLayers }: ViewerPanelProps) {
  const [layersOpen, setLayersOpen] = useState(() => {
    try { return localStorage.getItem("floorplan-layers") === "1"; } catch { return false; }
  });
  const toggleLayers = () => {
    const next = !layersOpen;
    setLayersOpen(next);
    try {
      localStorage.setItem("floorplan-layers", next ? "1" : "0");
    } catch {
      // Private browsing and blocked site data both throw here. The panel still
      // opens — it just will not be remembered across reloads.
    }
  };
  const [legendOpen, setLegendOpen] = useState(() => {
    try { return localStorage.getItem("floorplan-legend") === "1"; } catch { return false; }
  });
  const toggleLegend = () => {
    const next = !legendOpen;
    setLegendOpen(next);
    try {
      localStorage.setItem("floorplan-legend", next ? "1" : "0");
    } catch {
      // Same as the layers panel above: a failed write costs the preference, not
      // the interaction.
    }
  };

  const sectionHeader = (label: string, isOpen: boolean, onClick: () => void) => (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 4,
        padding: "4px 8px", width: "100%",
        background: "none", border: "none", cursor: "pointer",
        color: "#94a3b8", fontSize: 10, fontWeight: 600,
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}
    >
      {isOpen ? "▾" : "▸"} {label}
    </button>
  );

  const toggleRow = (
    label: string,
    checked: boolean,
    onChange: (v: boolean) => void,
    opts?: { indent?: number },
  ) => (
    <div
      key={label}
      onClick={() => onChange(!checked)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "1px 8px",
        paddingLeft: 8 + (opts?.indent ?? 0),
        cursor: "pointer",
        color: "#e2e8f0",
        userSelect: "none",
      }}
    >
      <span style={{ fontSize: opts?.indent ? 9 : undefined, opacity: opts?.indent ? 0.85 : 1 }}>{label}</span>
      <span
        style={{
          display: "inline-flex", alignItems: "center",
          width: 24, height: 14, borderRadius: 7,
          backgroundColor: checked ? "#3b82f6" : "rgba(148,163,184,0.3)",
          padding: 1, cursor: "pointer", transition: "background-color 0.15s",
          flexShrink: 0,
        }}
      >
        <span style={{
          width: 12, height: 12, borderRadius: "50%",
          backgroundColor: "#fff",
          transform: checked ? "translateX(10px)" : "translateX(0)",
          transition: "transform 0.15s",
        }} />
      </span>
    </div>
  );

  const colors = [
    { color: "#22c55e", label: "Available" },
    { color: "#ef4444", label: "Booked" },
    { color: "#3b82f6", label: "Occupied" },
    { color: "#06b6d4", label: "New neighbour" },
    ...(mgmt ? [
      { color: "#f59e0b", label: "Leaving" },
      { color: "#14b8a6", label: "Transition" },
    ] : []),
    { color: "#a78bfa", label: "Not connected" },
    { color: "#64748b", label: "Empty" },
  ];
  const icons = [
    { icon: "👥", label: "Seats" },
    { icon: "📷", label: "Camera" },
    { icon: "🖥", label: "Display" },
    { icon: "🔊", label: "Speaker" },
    { icon: "✨", label: "New neighbour" },
    { icon: "🎉", label: "New member" },
  ];
  // Mirrors DESK_FILL_BY_STATUS in furniture/shapes.tsx — keep in sync.
  // Match-tenant desks inherit the room's colour and aren't shown here.
  const deskLegend = [
    { fill: "#fed7aa", stroke: "#ea580c", label: "Different tenant" },
    { fill: "#ddd6fe", stroke: "#7c3aed", label: "Assigned to user" },
    { fill: "#e2e8f0", stroke: "#94a3b8", label: "Unassigned" },
  ];

  return (
    <div style={{
      position: "absolute", top: 6, right: 6, zIndex: 5,
      borderRadius: 6,
      background: "rgba(15,23,42,0.75)", backdropFilter: "blur(4px)",
      fontSize: 10, minWidth: 170,
    }}>
      {/* Layers */}
      {sectionHeader("Layers", layersOpen, toggleLayers)}
      {layersOpen && (
        <div style={{ padding: "0 0 2px" }}>
          {/* Per-room-type filter rows. Rendered indented under the master
              "Rooms" entry below so their visual nesting matches their
              functional nesting. Each row's checked state ANDs the master
              Rooms toggle so when the master is off they all read off too. */}
          {(() => {
            const renderRoomTypes = () =>
              roomTypes.map((rt) =>
                toggleRow(
                  rt,
                  showRoomOverlays && !hiddenRoomTypes.has(rt),
                  () => {
                    // Clicking a sub-type while master is off implicitly turns
                    // master on so the user gets visible feedback.
                    if (!showRoomOverlays) {
                      onToggleRoomOverlays(true);
                      onToggleM2(true);
                    }
                    onToggleRoomType(rt);
                  },
                  { indent: 12 },
                ),
              );

            // Registry-driven layers (when backend supplies them).
            // alwaysShow layers don't appear in the panel — they're forced on
            // and the user can't change them, so a list row would just be noise.
            if (availableLayers?.length) {
              return (
                <>
                  {availableLayers.filter((l) => !l.alwaysShow && l.parentId == null).map((l) => {
                    const onState = layerToShowState(l.type, {
                      rooms: showRoomOverlays,
                      addresses: showAddressLabels,
                      furniture: showFurniture,
                      equipment: showEquipment,
                      desks: showDesks,
                      connections: showConnections,
                      navigation: showNavigation,
                      svg_text: showSvgText,
                      outlets: showOutlets,
                      devices: showDevices,
                      tenant_names: showTenantNames,
                    });
                    const row = toggleRow(l.name, onState, (v) => {
                      switch (l.type) {
                        case "rooms":
                          onToggleRoomOverlays(v);
                          onToggleM2(v);
                          // Master Rooms toggle propagates to per-room-type
                          // checkboxes so they visually track the master state.
                          onSetAllRoomTypesVisible(v);
                          break;
                        case "addresses": onToggleAddressLabels(v); break;
                        case "furniture": onToggleFurniture(v); break;
                        case "equipment": onToggleEquipment(v); break;
                        case "desks": onToggleDesks(v); break;
                        case "connections": onToggleConnections(v); break;
                        case "navigation": onToggleNavigation(v); break;
                        case "svg_text": onToggleSvgText(v); break;
                        case "outlets": onToggleOutlets(v); break;
                        case "devices": onToggleDevices(v); break;
                        case "tenant_names": onToggleTenantNames(v); break;
                        // heatmap_* etc. — wired when their renderers land.
                      }
                    });
                    if (l.type === "rooms") {
                      return (
                        <Fragment key={l.id}>
                          {row}
                          {renderRoomTypes()}
                        </Fragment>
                      );
                    }
                    return row;
                  })}
                  {hasSvgText && !availableLayers.some((l) => l.type === "svg_text") && toggleRow("SVG Text", showSvgText, onToggleSvgText)}
                  {hasOccupancy && onToggleOccupancy && toggleRow("Occupancy", !!showOccupancy, onToggleOccupancy)}
                </>
              );
            }
            // Legacy hardcoded toggles when no registry was supplied. Same
            // layout: Rooms first, types indented under, then the rest.
            return (
              <>
                {toggleRow("Rooms", showRoomOverlays, (v) => {
                  onToggleRoomOverlays(v);
                  onToggleM2(v);
                  onSetAllRoomTypesVisible(v);
                })}
                {renderRoomTypes()}
                {toggleRow("Addresses", showAddressLabels, onToggleAddressLabels)}
                {toggleRow("Furniture", showFurniture, onToggleFurniture)}
                {toggleRow("Equipment", showEquipment, onToggleEquipment)}
                {toggleRow("Desks", showDesks, onToggleDesks)}
                {toggleRow("Connections", showConnections, onToggleConnections)}
                {hasNavigation && toggleRow("Navigation", showNavigation, onToggleNavigation)}
                {hasSvgText && toggleRow("SVG Text", showSvgText, onToggleSvgText)}
                {hasOccupancy && onToggleOccupancy && toggleRow("Occupancy", !!showOccupancy, onToggleOccupancy)}
              </>
            );
          })()}
        </div>
      )}

      <div style={{ borderTop: "1px solid rgba(148,163,184,0.15)", margin: "2px 0" }} />

      {/* Legend */}
      {sectionHeader("Legend", legendOpen, toggleLegend)}
      {legendOpen && (
        <div style={{ padding: "0 8px 6px", lineHeight: 1.7 }}>
          {colors.map((it) => (
            <div key={it.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: it.color, flexShrink: 0 }} />
              <span style={{ color: "#e2e8f0" }}>{it.label}</span>
            </div>
          ))}
          <div style={{ borderTop: "1px solid rgba(148,163,184,0.2)", margin: "3px 0" }} />
          {icons.map((it) => (
            <div key={it.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, textAlign: "center", flexShrink: 0 }}>{it.icon}</span>
              <span style={{ color: "#e2e8f0" }}>{it.label}</span>
            </div>
          ))}
          <div style={{ borderTop: "1px solid rgba(148,163,184,0.2)", margin: "3px 0" }} />
          <div style={{ color: "#94a3b8", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>Desks</div>
          {deskLegend.map((it) => (
            <div key={it.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: it.fill, border: `1px solid ${it.stroke}`, flexShrink: 0 }} />
              <span style={{ color: "#e2e8f0" }}>{it.label}</span>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, textAlign: "center", flexShrink: 0, color: "#7c3aed" }}>👤</span>
            <span style={{ color: "#e2e8f0" }}>User-assigned</span>
          </div>
        </div>
      )}
    </div>
  );
}

export interface FloorplanViewerHandle {
  zoomToRect: (minX: number, minY: number, maxX: number, maxY: number, padding?: number) => void;
  resetZoom: () => void;
  setView: (x: number, y: number, z: number) => void;
}

export const FloorplanViewer = forwardRef<FloorplanViewerHandle, FloorplanViewerProps>(
  function FloorplanViewer(
    { svg, rooms: roomsProp, selectedRoomId: selectedRoomIdProp, onRoomClick, onItemClick, onViewChange, scale: floorplanScale, floorplans, activeFloorplanId, onFloorplanChange, onAddressCreate, onAddressLabelClick, onRoomCreate, connectionZones, floors, searchable, onSearchResult, floorFurniture, doors, outlets, devices, onDeviceClick, availableLayers, showManagementDetails, hidePanel, syncUrl, svgOverlay, svgTextElements, navigationPaths, relatedRoomIds, alarmLocations, children },
    ref,
  ) {
    const viewBox = useMemo(() => parseViewBox(svg), [svg]);
    // #416: parts fold into their main room — one fill, one label, and a click on any part opens the main room.
    const rooms = useMemo(() => foldMergedRooms(roomsProp), [roomsProp]);
    // Alarm per drawn region (floor regions, room polygons), never over the whole plan.
    const alarmRegions = useMemo(
      () => resolveAlarmRegions(floors, roomsProp, alarmLocations),
      [floors, roomsProp, alarmLocations],
    );
    // A selected part highlights (and zooms to) its main room.
    const selectedRoomId = useMemo(() => resolveMergedRoomId(roomsProp, selectedRoomIdProp), [roomsProp, selectedRoomIdProp]);
    // #428: lease rooms highlighted with the selection; a part resolves to its main room.
    const relatedIds = useMemo(
      () => (relatedRoomIds ?? []).map((id) => resolveMergedRoomId(roomsProp, id)).filter((id): id is number => id != null),
      [roomsProp, relatedRoomIds],
    );

    // The floor (address region) picked in the room finder (internal state,
    // not passed from the parent); a picked room is `selectedRoomId`.
    const [pickerFloorId, setPickerFloorId] = useState<number | null>(null);

    // Reset the picked floor when the floorplan changes
    useEffect(() => { setPickerFloorId(null); }, [activeFloorplanId]);

    // URL sync refs (declared before hooks that use them)
    const urlTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasRestoredUrl = useRef(false);

    // Combined view change: notify parent + sync URL
    const combinedViewChange = useCallback((view: { x: number; y: number; z: number }) => {
      onViewChange?.(view);
      if (syncUrl) {
        if (urlTimerRef.current) clearTimeout(urlTimerRef.current);
        urlTimerRef.current = setTimeout(() => {
          const params = new URLSearchParams(window.location.search);
          params.set("x", String(view.x));
          params.set("y", String(view.y));
          params.set("z", String(view.z));
          // Clear room when user pans/zooms manually
          params.delete("room");
          window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
        }, 150);
      }
    }, [onViewChange, syncUrl]);

    const {
      containerRef,
      transform,
      scale: zoomScale,
      onMouseDown,
      onMouseMove,
      onMouseUp,
      zoomToRect,
      resetZoom,
      setView,
      wasDragging,
    } = useFloorplanZoom(viewBox, combinedViewChange);

    useImperativeHandle(ref, () => ({ zoomToRect, resetZoom, setView }), [zoomToRect, resetZoom, setView]);

    // Layer visibility: when the backend supplies availableLayers, derive
    // each show-flag from the registry's default_on / always_show. Without
    // a registry we fall back to the legacy hardcoded defaults.
    const layerByType = useMemo(() => {
      const m: Record<string, import("./types").FloorplanLayer> = {};
      for (const l of availableLayers ?? []) m[l.type] = l;
      return m;
    }, [availableLayers]);
    const layerInitial = (type: string, fallback: boolean): boolean => {
      const l = layerByType[type];
      if (!l) return availableLayers?.length ? false : fallback;
      if (l.alwaysShow) return true;
      return l.defaultOn;
    };
    const [showAddressLabels, setShowAddressLabels] = useState(() => layerInitial("addresses", true));
    const [showRoomOverlays, setShowRoomOverlays] = useState(() => layerInitial("rooms", true));
    // `layerInitial` resolves an unknown type to hidden once any registry is
    // present, which for a label modifier means silently blanking every
    // occupied room on a site whose registry predates this layer. Names are
    // the status quo, so this one falls back to visible instead.
    const [showTenantNames, setShowTenantNames] = useState(
      () => layerByType["tenant_names"]?.defaultOn ?? true,
    );
    const [showM2, setShowM2] = useState(true);
    const [showFurniture, setShowFurniture] = useState(() => layerInitial("furniture", true));
    const [showEquipment, setShowEquipment] = useState(() => layerInitial("equipment", true));
    const [showDesks, setShowDesks] = useState(() => layerInitial("desks", true));
    const [showConnections, setShowConnections] = useState(() => layerInitial("connections", true));
    const [showNavigation, setShowNavigation] = useState(() => layerInitial("navigation", false));
    const [showSvgText, setShowSvgText] = useState(false);
    const [showOutlets, setShowOutlets] = useState(() => layerInitial("outlets", false));
    const [showDevices, setShowDevices] = useState(() => layerInitial("devices", true));
    // Not a registry layer: offered whenever a room carries a sensor reading, on until switched off.
    const [showOccupancy, setShowOccupancy] = useState(true);
    // Track the rendered SVG width in CSS px so OutletOverlay can convert
    // the desired on-screen cluster radius into viewBox units. Wrap the
    // zoom hook's container ref so both run when the node mounts.
    const [renderedSvgPx, setRenderedSvgPx] = useState(0);
    const sizeObserverRef = useRef<ResizeObserver | null>(null);
    const wrappedContainerRef = useCallback(
      (node: HTMLDivElement | null) => {
        (containerRef as unknown as (n: HTMLDivElement | null) => void)(node);
        if (sizeObserverRef.current) sizeObserverRef.current.disconnect();
        sizeObserverRef.current = null;
        if (!node || typeof ResizeObserver === "undefined") {
          if (node) setRenderedSvgPx(node.getBoundingClientRect().width);
          return;
        }
        setRenderedSvgPx(node.getBoundingClientRect().width);
        const ro = new ResizeObserver(() => setRenderedSvgPx(node.getBoundingClientRect().width));
        ro.observe(node);
        sizeObserverRef.current = ro;
      },
      [containerRef],
    );
    const svgUnitsWide = useMemo(() => {
      if (!viewBox) return 0;
      const parts = viewBox.split(/\s+/).map(Number);
      return parts[2] || 0;
    }, [viewBox]);
    const [connectionPopup, setConnectionPopup] = useState<{ zone: ConnectionZone; x: number; y: number } | null>(null);
    const [hiddenRoomTypes, setHiddenRoomTypes] = useState<Set<string>>(new Set());

    // Per-room-type child layers from the registry: each has type="rooms"
    // and `filter.room_type` set to a Room.room_type display value. The
    // master Rooms layer has no filter. Falls back to deriving the list
    // from rooms data when no DB-driven children exist (legacy customers
    // who haven't run the seed migration yet).
    const roomsMasterLayer = useMemo(
      () =>
        (availableLayers ?? []).find(
          (l) => l.type === "rooms" && !(l.filter as { room_type?: string } | undefined)?.room_type,
        ) ?? null,
      [availableLayers],
    );
    const roomTypeChildLayers = useMemo(
      () =>
        (availableLayers ?? []).filter(
          (l) =>
            l.type === "rooms" &&
            !!(l.filter as { room_type?: string } | undefined)?.room_type &&
            (roomsMasterLayer?.id == null || l.parentId == null || l.parentId === Number(roomsMasterLayer.id)),
        ),
      [availableLayers, roomsMasterLayer],
    );
    // Unique room types — DB-driven when child layers exist, otherwise
    // derived from the rooms data so legacy installs keep working.
    const roomTypes = useMemo(() => {
      if (roomTypeChildLayers.length > 0) {
        return roomTypeChildLayers
          .slice()
          .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
          .map((l) => (l.filter as { room_type: string }).room_type);
      }
      const types = new Set<string>();
      for (const r of rooms) {
        if (r.roomType && r.polygon?.length && r.polygon[0]?.length) types.add(r.roomType);
      }
      return Array.from(types).sort();
    }, [rooms, roomTypeChildLayers]);

    // Filter rooms by visible room types.
    const visibleRooms = useMemo(() => {
      if (hiddenRoomTypes.size === 0) return rooms;
      return rooms.filter((r) => !r.roomType || !hiddenRoomTypes.has(r.roomType));
    }, [rooms, hiddenRoomTypes]);

    // The room finder's lists: the plan's floors per address, the drawn rooms under them.
    const pickerData = useMemo(() => viewerLocationData(rooms, floors), [rooms, floors]);

    // Sync arbitrary params to URL
    const syncToUrl = useCallback((updates: Record<string, string | null>) => {
      if (!syncUrl) return;
      const params = new URLSearchParams(window.location.search);
      for (const [k, v] of Object.entries(updates)) {
        if (v != null) params.set(k, v);
        else params.delete(k);
      }
      window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
    }, [syncUrl]);

    // Restore from URL on mount
    useEffect(() => {
      if (!syncUrl || hasRestoredUrl.current || !rooms.length) return;
      hasRestoredUrl.current = true;
      const p = new URLSearchParams(window.location.search);
      const urlRoom = p.get("room");
      const urlX = p.get("x");
      const urlY = p.get("y");
      const urlZ = p.get("z");
      const urlFp = p.get("fp");

      if (urlFp && onFloorplanChange && Number(urlFp) !== activeFloorplanId) {
        onFloorplanChange(Number(urlFp));
      }

      if (urlRoom) {
        // An old ?room= link to a merged part restores its main room.
        const urlRoomId = resolveMergedRoomId(roomsProp, Number(urlRoom));
        const room = rooms.find((r) => r.id === urlRoomId);
        if (room) {
          onRoomClick?.(room);
          setTimeout(() => {
            if (room.polygon?.[0]?.length) {
              const poly = roomZoomPoints(room);
              const xs = poly.map((pt: number[]) => pt[0]);
              const ys = poly.map((pt: number[]) => pt[1]);
              zoomToRect(Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys), 60);
            }
          }, 400);
          return;
        }
      }

      if (urlX && urlY && urlZ) {
        setTimeout(() => setView(Number(urlX), Number(urlY), Number(urlZ)), 300);
      }
    }, [syncUrl, rooms, roomsProp, activeFloorplanId, onFloorplanChange, onRoomClick, zoomToRect, setView]);

    const [svgWidth, svgHeight] = useMemo(() => {
      if (!viewBox) return [2000, 1400];
      const parts = viewBox.split(/\s+/).map(Number);
      return [parts[2] || 2000, parts[3] || 1400];
    }, [viewBox]);

    const sanitizedContent = useMemo(() => extractSvgContent(svg), [svg]);

    // Wrap onRoomClick to also zoom + sync URL
    const handleRoomClick = useMemo(() => {
      if (!onRoomClick) return undefined;
      return (room: FloorplanRoom) => {
        if (wasDragging.current) return; // suppress click after panning
        onRoomClick(room);
        syncToUrl({ room: String(room.id), x: null, y: null, z: null });
        if (room.polygon?.[0]?.length) {
          const poly = roomZoomPoints(room);
          const xs = poly.map((p: number[]) => p[0]);
          const ys = poly.map((p: number[]) => p[1]);
          zoomToRect(Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys), 60);
        }
      };
    }, [onRoomClick, zoomToRect, syncToUrl]);

    const zoomToRegion = (region: number[][] | undefined, padding: number): boolean => {
      if (!region || region.length < 3) return false;
      const xs = region.map((p) => p[0]);
      const ys = region.map((p) => p[1]);
      zoomToRect(Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys), padding);
      return true;
    };
    const zoomToRoom = (room: FloorplanRoom | undefined): boolean => {
      if (!room?.polygon?.[0]?.length) return false;
      const poly = roomZoomPoints(room);
      const xs = poly.map((p) => p[0]);
      const ys = poly.map((p) => p[1]);
      zoomToRect(Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys), 60);
      return true;
    };

    // The room finder: a room is the selection; a floor (an address's region
    // on this plan) zooms to its region.
    const finderValue: LocationRef | null =
      selectedRoomId != null ? { kind: "room", id: selectedRoomId } :
      pickerFloorId != null ? { kind: "floor", id: pickerFloorId } :
      null;
    const pickFromFinder = (ref: LocationRef | null) => {
      if (ref?.kind === "room") {
        setPickerFloorId(null);
        if (onSearchResult) onSearchResult(ref.id);
        const room = rooms.find((r) => r.id === ref.id);
        zoomToRoom(room);
        if (room && onRoomClick) onRoomClick(room);
        return;
      }
      setPickerFloorId(ref?.kind === "floor" ? ref.id : null);
      if (onSearchResult) onSearchResult(null);
      if (onRoomClick) onRoomClick(null);
      const floor = ref?.kind === "floor" ? floors?.find((f) => f.floorId === ref.id) : undefined;
      if (!zoomToRegion(floor?.region, 30)) resetZoom();
      syncToUrl({ room: null, x: null, y: null, z: null });
    };
    const pickFloorplan = (id: number) => {
      setPickerFloorId(null);
      if (onSearchResult) onSearchResult(null);
      if (onRoomClick) onRoomClick(null);
      if (onFloorplanChange && id !== activeFloorplanId) {
        onFloorplanChange(id);
        syncToUrl({ fp: String(id), room: null, x: null, y: null, z: null });
        return;
      }
      resetZoom();
      syncToUrl({ room: null, x: null, y: null, z: null });
    };
    // Zoom to the current selection level: room → floor region → the whole plan.
    const fitToSelection = () => {
      if (selectedRoomId != null && zoomToRoom(rooms.find((r) => r.id === selectedRoomId))) return;
      if (pickerFloorId != null && zoomToRegion(floors?.find((f) => f.floorId === pickerFloorId)?.region, 30)) return;
      resetZoom();
    };
    // Create actions act on the floor in view: the picked one, the picked
    // room's, else the plan's first.
    const pickedFloor = pickerFloorId != null ? floors?.find((f) => f.floorId === pickerFloorId) : undefined;
    const pickedRoom = selectedRoomId != null ? rooms.find((r) => r.id === selectedRoomId) : undefined;
    const currentFloor =
      pickedFloor ??
      (pickedRoom ? floors?.find((f) => f.floorName === pickedRoom.floorName && f.addressName === pickedRoom.addressName) : undefined) ??
      floors?.[0];
    const finderActions: LocationPickerAction[] = [
      ...(onRoomCreate ? [{ label: "Create room", onClick: () => onRoomCreate(currentFloor?.floorId ?? 0, currentFloor?.addressId) }] : []),
      ...(onAddressCreate ? [{ label: "Create address", onClick: () => onAddressCreate(currentFloor?.floorId ?? 0) }] : []),
    ];

    return (
      <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
        {/* Toolbar — outside zoom, fixed size */}
        {searchable ? (
          <Group gap={6} px={8} py={6} wrap="nowrap" flex="0 0 auto" {...darkSchemeScope}>
            {floorplans && floorplans.length > 1 && (
              <Select
                {...darkComboboxProps}
                size="xs"
                w={{ base: 110, sm: 150 }}
                miw={{ base: 110, sm: 150 }}
                aria-label="Floorplan"
                data={floorplans.map((fp) => ({ value: String(fp.id), label: fp.name }))}
                value={activeFloorplanId != null ? String(activeFloorplanId) : null}
                allowDeselect={false}
                onChange={(next) => next && pickFloorplan(Number(next))}
              />
            )}
            <Box miw={0} maw={500} flex={1}>
              <LocationPicker
                kinds={FINDER_KINDS}
                variant="dark"
                size="xs"
                ariaLabel="Find a room"
                placeholder="Find a room or floor…"
                nothingFoundMessage="No rooms on this plan match"
                floors={pickerData.floors}
                rooms={pickerData.rooms}
                value={finderValue}
                onChange={pickFromFinder}
                actions={finderActions}
              />
            </Box>
            <Tooltip label="Fit to view">
              <ActionIcon variant="default" size="input-xs" aria-label="Fit to view" onClick={fitToSelection}>
                <Maximize size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", flexShrink: 0 }} />
        )}
        {/* SVG canvas */}
        <div
          ref={wrappedContainerRef}
          style={{ overflow: "hidden", width: "100%", flex: 1, minHeight: 0, position: "relative", touchAction: "none" }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
        <div style={{ transform, transformOrigin: "0 0" }}>
          <svg
            viewBox={viewBox}
            width={svgWidth}
            height={svgHeight}
            style={{ display: "block" }}
          >
            {/* Background SVG content */}
            <g dangerouslySetInnerHTML={{ __html: sanitizedContent }} />

            {/* Address region labels */}
            {showAddressLabels && floors?.map((f) => {
              if (!f.region || f.region.length < 3) return null;
              return (
                <text
                  key={f.floorId}
                  x={Math.min(...f.region.map((p) => p[0])) + 6}
                  y={Math.max(...f.region.map((p) => p[1])) + 14}
                  fill="#94a3b8"
                  fontSize="11"
                  fontWeight="600"
                  style={{ pointerEvents: "auto", cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Zoom to address region + update picker
                    setPickerFloorId(f.floorId);
                    if (onSearchResult) onSearchResult(null);
                    if (onRoomClick) onRoomClick(null);
                    const xs = f.region.map((p: number[]) => p[0]);
                    const ys = f.region.map((p: number[]) => p[1]);
                    zoomToRect(Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys), 30);
                    syncToUrl({ room: null, x: null, y: null, z: null });
                    // Also call external handler if provided
                    onAddressLabelClick?.(f.addressName, f.floorId);
                  }}
                >
                  {f.addressName}
                </text>
              );
            })}

            {/* Room polygons — clickable backdrop. Painted first so
                furniture/desks render on top and receive their own clicks.
                Empty floor area inside a room still hits this polygon. */}
            {showRoomOverlays && (
              <FloorplanRoomOverlay
                rooms={visibleRooms}
                selectedRoomId={selectedRoomId}
                onRoomClick={handleRoomClick}
                showManagementDetails={showManagementDetails}
                relatedRoomIds={relatedIds.length ? relatedIds : undefined}
                mode="polygonsOnly"
              />
            )}

            {/* Furniture / equipment / desks — gated per backend model so
                each layer toggle hides exactly the rows that ended up in
                that table. alwaysVisible items (fire extinguisher, AED,
                booths) ignore these toggles. */}
            <FurnitureOverlay
              rooms={visibleRooms}
              scale={floorplanScale ?? 1}
              kindsVisible={{ furniture: showFurniture, equipment: showEquipment, desk: showDesks }}
              floorFurniture={floorFurniture}
              onRoomClick={handleRoomClick}
              onItemClick={onItemClick}
              showManagementDetails={showManagementDetails}
              showOccupancy={showOccupancy}
            />

            {/* Doors — Point icon + arrow heads off enabled sides. */}
            <FloorplanDoorMarkers doors={doors} />

            {/* Outlets — clustered port glyphs. Cluster radius shrinks
                as you zoom in so individual ports separate. */}
            {showOutlets && outlets && outlets.length > 0 && (
              <OutletOverlay
                outlets={outlets}
                zoom={zoomScale}
                svgUnitsWide={svgUnitsWide}
                renderedSvgPx={renderedSvgPx}
              />
            )}

            {/* Devices — AP / router / switch markers (role-gated server-side). */}
            {showDevices && devices && devices.length > 0 && (
              <DeviceOverlay devices={devices} onDeviceClick={onDeviceClick} />
            )}

            {/* Connection zones (elevator, stairs — not corridors) */}
            {showConnections && connectionZones?.filter((z) => z.type !== "corridor").map((zone) => {
              const colors: Record<string, string> = { elevator: "#3b82f6", stairs: "#f97316", entrance: "#22c55e" };
              const color = colors[zone.type] ?? "#94a3b8";
              const pts = zone.polygon.map((p) => p.join(",")).join(" ");
              if (!pts) return null;
              const xs = zone.polygon.map((p) => p[0]);
              const ys = zone.polygon.map((p) => p[1]);
              const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
              const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
              return (
                <g key={zone.id} style={{ cursor: "pointer" }} onClick={(e) => {
                  e.stopPropagation();
                  setConnectionPopup({ zone, x: cx, y: cy });
                }}>
                  <polygon
                    points={pts}
                    fill={zone.outOfService ? "#ef4444" : color}
                    fillOpacity={zone.outOfService ? 0.15 : 0.25}
                    stroke={zone.outOfService ? "#ef4444" : color}
                    strokeWidth={2}
                    strokeDasharray={zone.outOfService ? "6,4" : undefined}
                  />
                  <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700} fill={zone.outOfService ? "#ef4444" : color} style={{ pointerEvents: "none" }}>
                    {zone.type === "elevator" ? "🛗" : "🪜"} {zone.name}{zone.outOfService ? " — out of service" : ""}
                  </text>
                </g>
              );
            })}

            {/* m² labels */}
            {showM2 && showRoomOverlays && <FloorplanAreaLabels rooms={visibleRooms} />}

            {showNavigation && navigationPaths?.map((path, i) => (
              <polyline
                key={`nav-${i}`}
                points={path.map((p) => p.join(",")).join(" ")}
                fill="none"
                stroke="#f97316"
                strokeWidth={1.5}
                strokeDasharray="4,3"
                opacity={0.6}
              />
            ))}

            {/* Room labels — painted AFTER furniture so room names always
                stay readable even when desks/furniture sit inside the room.
                Polygons are still painted earlier as a clickable backdrop. */}
            {showRoomOverlays && (
              <FloorplanRoomOverlay
                rooms={visibleRooms}
                selectedRoomId={selectedRoomId}
                onRoomClick={handleRoomClick}
                showManagementDetails={showManagementDetails}
                showTenantNames={showTenantNames}
                mode="labelsOnly"
              />
            )}

            {showSvgText && svgTextElements && (
              <g dangerouslySetInnerHTML={{
                __html: svgTextElements /* sanitized server-side via clean_svg */,
              }} />
            )}

            {/* Alarm: a light tint + outline on each alarmed region only (clicks pass through). */}
            <FloorplanAlarmRegions regions={alarmRegions} />

            {/* Occupancy: rooms a presence sensor says are in use (clicks pass through). */}
            {showOccupancy && <FloorplanBusyRooms rooms={roomsProp ?? []} mgmt={!!showManagementDetails} />}

            {svgOverlay}
          </svg>

          {children}

          {/* Connection zone popup */}
          {connectionPopup && (
            <div
              style={{
                position: "absolute",
                top: 60,
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(15, 23, 42, 0.95)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(148,163,184,0.3)",
                borderRadius: 8,
                padding: "12px 16px",
                fontSize: 13,
                color: "#e2e8f0",
                zIndex: 30,
                minWidth: 180,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{connectionPopup.zone.type === "elevator" ? "🛗" : "🪜"} {connectionPopup.zone.name}</span>
                <button onClick={() => setConnectionPopup(null)} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 16 }}>×</button>
              </div>
              {connectionPopup.zone.outOfService && (
                <div style={{ marginBottom: 8, padding: "4px 8px", borderRadius: 4, background: "rgba(239,68,68,0.2)", color: "#fca5a5", fontWeight: 600 }}>
                  Out of service{connectionPopup.zone.outOfServiceNote ? ` — ${connectionPopup.zone.outOfServiceNote}` : ""}
                </div>
              )}
              {(() => {
                // Only the plans where THIS connection has a zone — never every plan of the site.
                const zone = connectionPopup.zone;
                const stops: ConnectionStop[] = zone.stops
                  ?? (floorplans ?? [])
                    .filter((fp) => fp.id === activeFloorplanId || (zone.targetFloorplanIds ?? []).includes(fp.id))
                    .map((fp) => ({ floorplanId: fp.id, label: fp.name }));
                if (stops.length <= 1) {
                  return <div style={{ color: "#94a3b8", fontSize: 12 }}>Not linked to any other floor yet.</div>;
                }
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {stops.map((stop) => {
                      const isCurrent = stop.floorplanId === activeFloorplanId;
                      return (
                        <button
                          key={stop.floorplanId}
                          onClick={() => {
                            if (!isCurrent && onFloorplanChange) {
                              onFloorplanChange(stop.floorplanId);
                              setConnectionPopup(null);
                            }
                          }}
                          style={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            padding: "4px 8px",
                            fontSize: 13,
                            background: isCurrent ? "rgba(59,130,246,0.2)" : "none",
                            border: "none",
                            borderRadius: 4,
                            color: isCurrent ? "#93c5fd" : "#e2e8f0",
                            cursor: isCurrent ? "default" : "pointer",
                            fontWeight: isCurrent ? 600 : 400,
                          }}
                          onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.backgroundColor = "rgba(148,163,184,0.15)"; }}
                          onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.backgroundColor = "transparent"; }}
                        >
                          {stop.label}{isCurrent ? " (you are here)" : ""}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
        {/* Alarm legend: outside the zoom transform, so each region's label stays put while panning. */}
        <FloorplanAlarmLegend regions={alarmRegions} />
        {showOccupancy && (hasOccupancyData(roomsProp) || hasItemOccupancy(roomsProp, floorFurniture)) && <FloorplanOccupancyLegend />}
        {!hidePanel && <ViewerPanel
          mgmt={showManagementDetails}
          showRoomOverlays={showRoomOverlays}
          onToggleRoomOverlays={setShowRoomOverlays}
          showAddressLabels={showAddressLabels}
          onToggleAddressLabels={setShowAddressLabels}
          showM2={showM2}
          onToggleM2={setShowM2}
          showFurniture={showFurniture}
          onToggleFurniture={setShowFurniture}
          showEquipment={showEquipment}
          onToggleEquipment={setShowEquipment}
          showDesks={showDesks}
          onToggleDesks={setShowDesks}
          showConnections={showConnections}
          onToggleConnections={setShowConnections}
          showNavigation={showNavigation}
          onToggleNavigation={setShowNavigation}
          showSvgText={showSvgText}
          onToggleSvgText={setShowSvgText}
          showOutlets={showOutlets}
          showDevices={showDevices}
          onToggleDevices={setShowDevices}
          hasOccupancy={hasOccupancyData(roomsProp) || hasItemOccupancy(roomsProp, floorFurniture)}
          showOccupancy={showOccupancy}
          onToggleOccupancy={setShowOccupancy}
          onToggleOutlets={setShowOutlets}
          showTenantNames={showTenantNames}
          onToggleTenantNames={setShowTenantNames}
          hasNavigation={!!navigationPaths?.length}
          hasSvgText={!!svgTextElements}
          roomTypes={roomTypes}
          hiddenRoomTypes={hiddenRoomTypes}
          onToggleRoomType={(rt) => setHiddenRoomTypes((prev) => {
            const next = new Set(prev);
            if (next.has(rt)) next.delete(rt); else next.add(rt);
            return next;
          })}
          onSetAllRoomTypesVisible={(visible) => {
            // visible=true → clear hidden set (all sub-toggles on)
            // visible=false → put every known type in the hidden set
            //                 (all sub-toggles off, mirroring master)
            setHiddenRoomTypes(visible ? new Set() : new Set(roomTypes));
          }}
          availableLayers={availableLayers}
        />}
      </div>
      </div>
    );
  },
);
